import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Download, Sun, Moon, Zap, Euro, TrendingUp, AlertTriangle } from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell, Tooltip,
} from "recharts";
import PageHeader from "@/components/PageHeader";
import { getOverview, getMonths, eur, kwh, pdfUrl, cregPdfUrl, monthLabel } from "@/lib/api";
import { Button } from "@/components/ui/button";

function Kpi({ icon: Icon, label, value, mono = true, tone = "primary" }) {
  const toneMap = {
    primary: "text-primary",
    day: "text-day",
    night: "text-night",
  };
  return (
    <div className="border border-border bg-card rounded-lg p-6">
      <div className="flex items-center gap-2 text-muted-foreground mb-3">
        <Icon className={`h-4 w-4 ${toneMap[tone]}`} />
        <span className="text-xs font-semibold uppercase tracking-[0.15em]">{label}</span>
      </div>
      <div className={`${mono ? "font-mono" : "font-head"} text-3xl font-bold tracking-tight`}>
        {value}
      </div>
    </div>
  );
}

function CompanyCard({ c, month }) {
  const data = [
    { name: "Dag", value: c.day_kwh, fill: "hsl(var(--day))" },
    { name: "Nacht", value: c.night_kwh, fill: "hsl(var(--night))" },
  ];
  const download = () => {
    window.open(pdfUrl(month, c.id_token), "_blank");
  };
  const downloadCreg = () => {
    window.open(cregPdfUrl(month, c.id_token), "_blank");
  };
  return (
    <div className="border border-border bg-card rounded-lg p-6 flex flex-col" data-testid={`company-card-${c.id_token}`}>
      <div className="flex items-start justify-between gap-4 mb-5">
        <div>
          <h3 className="font-head text-xl font-bold tracking-tight leading-tight">{c.company_name}</h3>
          <p className="text-xs text-muted-foreground mt-1">
            {c.session_count} laadsessie{c.session_count === 1 ? "" : "s"} · {monthLabel(month)}
          </p>
          {c.suppliers && c.suppliers.length > 0 && (
            <p className="text-xs text-muted-foreground mt-0.5">Leverancier: {c.suppliers.join(", ")}</p>
          )}
        </div>
        <div className="text-right">
          <div className="font-mono text-2xl font-bold text-primary">{eur(c.total_cost)}</div>
          <div className="text-xs text-muted-foreground mt-0.5">{kwh(c.total_kwh)} kWh</div>
        </div>
      </div>

      {c.missing_tariff && (
        <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 text-destructive px-3 py-2 mb-4 text-xs" data-testid={`missing-tariff-${c.id_token}`}>
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
          <span>{kwh(c.unpriced_kwh)} kWh zonder tarief. Stel een tarief in met de juiste ingangsdatum om te kunnen exporteren.</span>
        </div>
      )}

      <div className="grid grid-cols-2 gap-3 mb-5">
        <div className="rounded-md border border-border/70 p-4">
          <div className="flex items-center gap-1.5 text-day mb-2">
            <Sun className="h-4 w-4" />
            <span className="text-[11px] font-semibold uppercase tracking-wider">Dag</span>
          </div>
          <div className="font-mono text-lg font-semibold">{kwh(c.day_kwh)} <span className="text-xs text-muted-foreground">kWh</span></div>
          <div className="font-mono text-sm text-muted-foreground">{eur(c.day_cost)}</div>
        </div>
        <div className="rounded-md border border-border/70 p-4">
          <div className="flex items-center gap-1.5 text-night mb-2">
            <Moon className="h-4 w-4" />
            <span className="text-[11px] font-semibold uppercase tracking-wider">Nacht</span>
          </div>
          <div className="font-mono text-lg font-semibold">{kwh(c.night_kwh)} <span className="text-xs text-muted-foreground">kWh</span></div>
          <div className="font-mono text-sm text-muted-foreground">{eur(c.night_cost)}</div>
        </div>
      </div>

      {c.total_kwh > 0 && (
        <div className="h-24 mb-5 -ml-2">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} layout="vertical" margin={{ left: 8, right: 16 }}>
              <XAxis type="number" hide />
              <YAxis type="category" dataKey="name" width={44}
                tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
              <Tooltip
                cursor={{ fill: "hsl(var(--muted))" }}
                contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }}
                formatter={(v) => [`${kwh(v)} kWh`, ""]}
              />
              <Bar dataKey="value" radius={[0, 4, 4, 0]} barSize={22}>
                {data.map((d, i) => <Cell key={i} fill={d.fill} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      <div className="mt-auto grid grid-cols-1 sm:grid-cols-2 gap-2">
        <Button
          variant="outline"
          className="gap-2 w-full"
          onClick={download}
          data-testid={`export-pdf-own-${c.id_token}`}
          disabled={c.session_count === 0 || c.missing_tariff}
        >
          <Download className="h-4 w-4" />
          Eigen tarief
        </Button>
        <Button
          className="gap-2 w-full"
          onClick={downloadCreg}
          data-testid={`export-pdf-creg-${c.id_token}`}
          disabled={c.session_count === 0}
        >
          <Download className="h-4 w-4" />
          CREG-tarief
        </Button>
      </div>
      <p className="text-[11px] text-muted-foreground mt-2 text-center">Exporteer onkostenvergoeding (PDF)</p>
    </div>
  );
}

export default function Overview() {
  const { data: months } = useQuery({ queryKey: ["months"], queryFn: getMonths });
  const [month, setMonth] = useState("");

  useEffect(() => {
    if (months && months.length && !month) setMonth(months[0]);
  }, [months, month]);

  const { data, isLoading } = useQuery({
    queryKey: ["overview", month],
    queryFn: () => getOverview(month),
    enabled: !!month,
  });

  return (
    <div>
      <PageHeader subtitle="Facturatie" title="Overzicht" month={month} setMonth={setMonth} />

      {!month && (
        <div className="border border-dashed border-border rounded-lg p-12 text-center text-muted-foreground">
          Nog geen gegevens. Klik op <span className="font-semibold text-foreground">Synchroniseer</span> om laadsessies in te laden.
        </div>
      )}

      {month && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
            <Kpi icon={Euro} label="Totale kosten" value={eur(data?.total_cost)} />
            <Kpi icon={Zap} label="Totaal verbruik" value={`${kwh(data?.total_kwh)} kWh`} />
            <Kpi icon={TrendingUp} label="Bedrijven" value={data?.companies?.length ?? 0} mono={false} />
          </div>

          {data && !data.has_tariffs && (
            <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 text-destructive px-4 py-3 mb-6 text-sm" data-testid="no-tariffs-banner">
              <AlertTriangle className="h-5 w-5 mt-0.5 shrink-0" />
              <span>Er is nog geen tarief ingesteld. Ga naar <b>Instellingen → Tarieven per periode</b> om je energietarief in te geven.</span>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {isLoading && [0, 1].map((i) => (
              <div key={i} className="border border-border bg-card rounded-lg p-6 h-72 animate-pulse" />
            ))}
            {!isLoading && data?.companies?.map((c) => (
              <CompanyCard key={c.id_token} c={c} month={month} />
            ))}
          </div>

          {data?.last_sync && (
            <p className="text-xs text-muted-foreground mt-8">
              Laatste synchronisatie: {new Date(data.last_sync).toLocaleString("nl-BE")}
            </p>
          )}
        </>
      )}
    </div>
  );
}
